import { Trans } from "@lingui/macro"
import { array, option } from "fp-ts"
import { identity, pipe } from "fp-ts/function"
import { AnimatePresence, motion } from "framer-motion"
import { Fragment } from "react"
import NotificationIcon from "remixicon-react/Notification4FillIcon"
import NotificationOffIcon from "remixicon-react/NotificationOffFillIcon"

import { useAuthorWatchdogState } from "@/domains/author-watchdogs/query"
import { AuthorDetail } from "@/domains/authors/model"
import * as Century from "@/domains/centuries"
import * as Country from "@/domains/countries"
import * as Tag from "@/domains/tags"
import { useI18n } from "@/lib/i18n"
import { nthCentury } from "@/lib/messages"
import { Box } from "@/ui/box"
import { Button } from "@/ui/button"
import { Gallery } from "@/ui/gallery"
import { Grid } from "@/ui/grid"
import { TextOverflowContainer } from "@/ui/overflow-text-container"
import { Prose } from "@/ui/prose"
import { Separator } from "@/ui/separator"
import { ParagraphSkeleton, Skeleton } from "@/ui/skeleton"
import { Stack } from "@/ui/stack"
import { Text, Title } from "@/ui/text"

function Tags({ author }: { author: AuthorDetail }) {
  const { localize } = useI18n()

  const countryTags: Array<Tag.TagViewProps> = pipe(
    option.fromNullable(author.country),
    option.map((c) => ({
      name: localize(c.name) ?? "",
      href: Country.hrefOf(c),
    })),
    option.match(
      () => [],
      (c) => [c]
    )
  )

  const centuriesTags: Array<Tag.TagViewProps> = pipe(
    option.fromNullable(author.centuries),
    option.map((array) =>
      array.map((c) => ({
        name: nthCentury(c),
        href: Century.hrefOf(c),
      }))
    ),
    option.match(() => [], identity)
  )

  const authorTags: Array<Tag.TagViewProps> = pipe(
    option.fromNullable(author.tags),
    option.map(
      array.map((t) => ({
        name: localize(t.name) ?? "",
        href: Tag.hrefOf(t),
      }))
    ),
    option.match(() => [], identity)
  )

  const tags = pipe(
    countryTags,
    array.concat(centuriesTags),
    array.concat(authorTags)
  )

  return (
    <Stack wrap align="stretch" rowGap={1} columnGap={3}>
      {tags.map((tag, index) => (
        <Fragment key={tag.name}>
          <Tag.TagView {...tag} />
          {index < tags.length - 1 && <Separator orientation="vertical" />}
        </Fragment>
      ))}
    </Stack>
  )
}

function LoadingDetails() {
  return (
    <>
      <Skeleton text css={{ height: 20, width: "50%" }} />
      <Skeleton text css={{ height: 80, mb: "$4" }} />
      <Skeleton text css={{ height: 24, width: "50%" }} />
      <ParagraphSkeleton lines={6} />
    </>
  )
}

function Details({ author }: { author: AuthorDetail }) {
  const { localize } = useI18n()
  const watchdogState = useAuthorWatchdogState(author.id)

  return (
    <>
      <Tags author={author} />
      <Text
        as="h1"
        size={{ "@initial": "4xl", "@md": "5xl" }}
        weight="bold"
        css={{ mb: "$4" }}
      >
        {author.name}
      </Text>
      <Stack align="center" justify="between" css={{ mb: "$2" }}>
        <Title>
          <Trans id="authorDetail.biography">Biografie</Trans>
        </Title>
        <AnimatePresence>
          {watchdogState.status === "success" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Button
                variant="ghost"
                iconBefore={
                  watchdogState.isOn ? (
                    <NotificationOffIcon />
                  ) : (
                    <NotificationIcon />
                  )
                }
                onClick={watchdogState.toggle}
              >
                {watchdogState.isOn ? (
                  <Trans id="authorDetail.watchAuthorButton.title">
                    Zrušit hlídání autora
                  </Trans>
                ) : (
                  <Trans id="authorDetail.unwatchAuthorButton.title">
                    Hlídat autora
                  </Trans>
                )}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </Stack>
      <TextOverflowContainer maxHeight={360}>
        <Prose color="muted" size="lg">
          {localize(author.description)}
        </Prose>
      </TextOverflowContainer>
    </>
  )
}

export function Header({ author }: { author?: AuthorDetail }) {
  return (
    <Grid
      columns={{ "@initial": 1, "@lg": 2 }}
      columnGap={12}
      rowGap={6}
      css={{ mb: "$8" }}
    >
      <Box>
        <Gallery
          items={author?.images.map((image) => ({
            image,
            alt: author.name,
          }))}
        />
      </Box>
      <Stack
        direction="column"
        gap={2}
        css={{
          zIndex: 10,
        }}
      >
        {author ? <Details author={author} /> : <LoadingDetails />}
      </Stack>
    </Grid>
  )
}
